import { Button, Typography } from '@mui/material';
import StoryDialog from '~/components/StoryDialog';
import { onValue, query } from 'firebase/database';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import AddAddress from 'containers/Checkout/AddAddress';
import ChooseAddress from 'containers/Checkout/ChooseAddress';
import { AddressPreview, InputField } from 'containers/Checkout/DeliveryDetails/index.styled';
import { FbUserContext } from 'context/FbUserContext';

import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { getUserAddressesRef } from 'services/FirebaseRefs';
import { saveAddressToCart } from 'services/ShoppingCart';
import { cartSelector, isLoadingSelector } from 'store/cart/selectors';
import { openModalSignIn } from 'store/signinInvitation/signinInvitationSlice';

import { Address } from 'models/address.interface';
import { getNewestAddress } from 'utils/address';
import { formatAddress } from 'utils/formatters';

enum DialogType {
  Add = 'Add',
  Choose = 'Choose',
}

const DeliveryDetails = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { fbUser } = useContext(FbUserContext);
  const { deliveryAddress } = useAppSelector(cartSelector);
  const isLoadingCart = useAppSelector(isLoadingSelector);
  const [instructions, setInstructions] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<DialogType>(DialogType.Add);
  const [addresses, setAddresses] = useState<Record<string, Address>>({});
  const displayAddress = deliveryAddress && Object.keys(deliveryAddress).length
    ? deliveryAddress
    : getNewestAddress(addresses);

  useEffect(() => {
    if (!instructions && deliveryAddress?.instructions !== instructions) {
      setInstructions(deliveryAddress?.instructions || '');
    }
  }, [deliveryAddress?.instructions]);

  useEffect(() => {
    if (!deliveryAddress && Object.keys(addresses)?.length && !isLoadingCart) {
      const address = getNewestAddress(addresses);

      if (address && fbUser) {
        saveAddressToCart(address, fbUser);
      }
    }
  }, [deliveryAddress, addresses, fbUser, isLoadingCart]);

  useEffect(() => {
    if (fbUser?.uid) {
      const refQuery = query(getUserAddressesRef(fbUser?.uid));
      onValue(
        refQuery,
        (dataSnapshot) => {
          const addresses = dataSnapshot.val() || {};
          setAddresses(addresses);
        },
        () => setAddresses({}));
    }
  }, [fbUser?.uid]);

  const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setInstructions(value);
  };

  const saveDeliveryInstructions = () => {
    if (!fbUser?.uid) {
      toast.error(intl.formatMessage({ id: 'error.missingUserId' }));
      return;
    }
    
    saveAddressToCart({ ...deliveryAddress, instructions }, fbUser);
  };

  const handleOpenDialog = (type: DialogType) => {
    if (fbUser?.isAnonymous) {
      dispatch(openModalSignIn());
      return;
    }

    setIsDialogOpen(true);
    setDialogType(type);
  };

  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleAddAddress = (address: Address) => {
    if (!fbUser?.uid) {
      toast.error(intl.formatMessage({ id: 'error.missingUserId' }));
      return;
    }
    
    saveAddressToCart({ ...address, instructions }, fbUser);
    handleCloseDialog();
  };

  return (
    <div>
      {displayAddress
        ? (
          <>
            <Typography variant="body1" mb={1.5}>
              {deliveryAddress?.name}
            </Typography>
            <AddressPreview
              variant="body1"
              fontWeight={600}
              color="primary"
              onClick={() => handleOpenDialog(DialogType.Choose)}
            >
              {formatAddress(displayAddress)}
            </AddressPreview>
            <Typography variant="body1" mb={1.5}>
              {intl.formatMessage({ id: 'label.notesForDriver' })}:
            </Typography>
            <InputField
              name="instructions"
              variant="outlined"
              onChange={handleChange}
              onBlur={saveDeliveryInstructions}
              value={instructions}
              placeholder={intl.formatMessage({ id: 'placeholder.typeYourMessageHere' })}
              fullWidth
            />
          </>
        ) : (
          <Button variant="text" onClick={() => handleOpenDialog(DialogType.Add)}>
            {intl.formatMessage({ id: 'button.addAddress' })}
          </Button>
        )}

      <StoryDialog isOpen={isDialogOpen} onClose={handleCloseDialog}>
        {dialogType === DialogType.Choose && deliveryAddress && (
          <ChooseAddress
            addresses={addresses}
            currentAddress={deliveryAddress}
            onClose={handleCloseDialog}
            onAddAddress={() => handleOpenDialog(DialogType.Add)}
          />
        )}
        {dialogType === DialogType.Add && fbUser && (
          <AddAddress userId={fbUser?.uid} onSuccess={handleAddAddress} />
        )}
      </StoryDialog>
    </div>
  );
};

export default DeliveryDetails;
